import React, { useState } from "react";
import { ArrowRight, Zap, Users, Smile } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

export default function HeroWithFeatures({
  product,
  currentImageIndex,
  prevImage,
  nextImage,
}) {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const features = [
    {
      id: 1,
      icon: <Zap className="w-8 h-8" />,
      title: "Quick Change",
      desc: "Wear and remove without taking shoes off",
    },
    {
      id: 2,
      icon: <Users className="w-8 h-8" />,
      title: "All Day Comfort",
      desc: "Perfect for any lifestyle or activity",
    },
    {
      id: 3,
      icon: <Smile className="w-8 h-8" />,
      title: "Pure Freedom",
      desc: "Express yourself with endless possibilities",
    },
  ];

  const getFeatureCardStyles = (featureId, index) => {
    const isHovered = hoveredFeature === featureId;
    return {
      borderColor: isHovered ? "#0165FC" : "#e5e7eb",
      backgroundColor: isHovered ? "#f0f7ff" : "#ffffff",

      transitionDelay: `${index * 100}ms`,
    };
  };

  const getIconStyles = (featureId) => {
    const isHovered = hoveredFeature === featureId;
    return {
      backgroundColor: isHovered ? "#0A2472" : "#f3f4f6",
      color: isHovered ? "white" : "#4b5563",
    };
  };

  return (
    <>
      {/* Hero Section */}
      <div className="min-h-screen flex items-center justify-center px-6 sm:px-12">
        <div className="max-w-7xl w-full grid md:grid-cols-2 gap-16 items-center">
          <div className="order-2 md:order-1 relative w-full overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {product.images.map((img, index) => (
                <div
                  key={index}
                  className="w-full flex-shrink-0 flex justify-center"
                >
                  <img
                    src={img}
                    alt={`ZipNZip Joggers ${index + 1}`}
                    className="w-[500px] mt-20 max-w-full"
                  />
                </div>
              ))}
            </div>
            {/* 
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow p-3 rounded-full transition"
            >
              ‹
            </button>

            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow p-3 rounded-full transition"
            >
              ›
            </button> */}
          </div>

          <div className="order-1 md:order-2">
            <h1
              className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8 tracking-tight sm:mt-0 mt-28 inline-block"
              style={{
                animation: "float 3s ease-in-out infinite",
              }}
            >
              Living active made <br />
              <span className="text-[#0A2472]">easier</span>
            </h1>
            <p className="text-lg md:text-l text-gray-600 mb-12 max-w-md">
              Introducing zipNzip - joggers with side zippers. Wear and remove
              without taking your shoes off.
            </p>

            <button
              onClick={() => navigate("/shop")}
              className="group inline-flex items-center gap-3 bg-black text-white rounded-md px-10 py-5 text-base font-medium hover:bg-gray-800 transition"
            >
              Shop Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Watch It In Action */}
      <section className="py-20 px-6 sm:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Watch It In Action
            </h2>
            <p className="text-lg text-gray-600">
              See how ZipNZip makes changing easier.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {product.prodVideos.map((video, index) => (
              <div
                key={index}
                className="rounded-2xl overflow-hidden shadow-lg bg-white"
              >
                <video
                  className="w-full h-full"
                  controls
                  // autoPlay
                  // muted
                  loop
                  playsInline
                  preload="auto"
                >
                  <source src={video.src} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose ZipNZip Section */}
      <section className="py-24 px-6 sm:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose zipNzip?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Engineering meets comfort in every pair
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Tilt
                key={feature.id}
                tiltMaxAngleX={10}
                tiltMaxAngleY={10}
                perspective={1000}
                scale={1.03}
                transitionSpeed={1000}
                glareEnable={true}
                glareMaxOpacity={0.08}
                glareColor="#ffffff"
                glarePosition="all"
              >
                <div
                  onMouseEnter={() => setHoveredFeature(feature.id)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  style={getFeatureCardStyles(feature.id, index)}
                  className="p-8 rounded-2xl border-2 transition-all duration-300 cursor-pointer h-full"
                >
                  <div
                    style={getIconStyles(feature.id)}
                    className="w-14 h-14 rounded-lg flex items-center justify-center mb-4 transition-all duration-300"
                  >
                    {feature.icon}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>

                  <p className="text-gray-600">{feature.desc}</p>
                </div>
              </Tilt>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
